import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import { getNormalPerformanceAutoRefreshWarningState } from "../contributors-data-integrator.utils"

const NORMAL_PERFORMANCE_NO_LIVE_PRICING_REFRESH_WARNING = "Extended date mode — live performance updates paused for ranges over 1 month. Switch to a shorter window for live updates."

interface PerformanceGatherer {
  type?: string
  prefix?: string
}

interface UseNormalPerformancePricingRefreshWarningArgs {
  gatherers?: PerformanceGatherer[] | null
  startDate?: string | null
  endDate?: string | null
  enabled?: boolean
}

let lastShownSignature: string | null = null

export function useNormalPerformancePricingRefreshWarning({
  gatherers,
  startDate,
  endDate,
  enabled = true,
}: UseNormalPerformancePricingRefreshWarningArgs) {
  const dispatch = useDispatch()
  const dashboardId = useDashboardId()
  const mainInstrumentId = useAppSelector((state) => state.dashboard.core[dashboardId]?.instrument?.id)

  const nonIntradayGatherers = useMemo(
    () => (gatherers || []).filter((gatherer) => gatherer.type !== "intraday"),
    [gatherers],
  )

  useEffect(() => {
    if (!enabled) {
      return
    }
    const { shouldShowWarning, nextShownSignature } = getNormalPerformanceAutoRefreshWarningState({
      nonIntradayGatherers,
      mainInstrumentId,
      startDate,
      endDate,
      lastShownSignature,
    })

    lastShownSignature = nextShownSignature

    if (shouldShowWarning) {
      dispatch(NotificationCreators.add("warning", null, NORMAL_PERFORMANCE_NO_LIVE_PRICING_REFRESH_WARNING, true))
    }
  }, [dispatch, enabled, endDate, mainInstrumentId, nonIntradayGatherers, startDate])
}
